import { useEffect, useState } from 'react';
import api from '../api/axios';
import styles from './Table.module.css';

const STATUS_COLORS = {
  open:        { bg: '#fff8e1', color: '#e6a817' },
  in_progress: { bg: '#e8f4ff', color: '#2563eb' },
  resolved:    { bg: '#e6f9f0', color: '#1a9e5c' },
};

const STATUS_LABELS = {
  open:        '🟡 Open',
  in_progress: '🔵 In Progress',
  resolved:    '✅ Resolved',
};

const CATEGORIES = [
  'Account Issue',
  'Ad Not Showing',
  'Image Upload Problem',
  'Payment / Package',
  'Report Abuse',
  'Other',
];

export default function SupportTickets() {
  const [tickets, setTickets]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Detail modal
  const [selected, setSelected] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving]     = useState(false);

  const load = async (p = page, s = statusFilter, c = categoryFilter, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (s) params.append('status', s);
      if (c) params.append('category', c);
      if (q) params.append('q', q);
      const res = await api.get(`/support/admin?${params}`);
      setTickets(res.data.tickets);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(page, statusFilter, categoryFilter, search); }, // eslint-disable-line
    [page, statusFilter, categoryFilter]); // eslint-disable-line

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, statusFilter, categoryFilter, search); }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const openDetail = (ticket) => {
    setSelected(ticket);
    setNoteText(ticket.adminNote || '');
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/support/admin/${selected._id}`, {
        status: selected.status,
        adminNote: noteText,
      });
      setSelected(null);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ticket permanently?')) return;
    try {
      await api.delete(`/support/admin/${id}`);
      setSelected(null);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Error');
    }
  };

  const statusBadge = (s) => {
    const c = STATUS_COLORS[s] || { bg: '#eee', color: '#888' };
    return (
      <span style={{
        background: c.bg, color: c.color,
        padding: '3px 10px', borderRadius: 12,
        fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
      }}>
        {STATUS_LABELS[s] || s}
      </span>
    );
  };

  const pages = Math.ceil(total / 20);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0 }}>
          Support Tickets{' '}
          <span style={{ fontSize: 14, color: '#888', fontWeight: 400 }}>({total})</span>
        </h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            className={styles.searchInput}
            placeholder="Search name, email, subject..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            value={categoryFilter}
            onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className={styles.center}>Loading...</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>From</th>
                <th>Category</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 && (
                <tr><td colSpan={7} className={styles.empty}>No tickets found</td></tr>
              )}
              {tickets.map((t, i) => (
                <tr key={t._id}>
                  <td style={{ color: '#aaa', fontSize: 12 }}>
                    #{t._id.slice(-6).toUpperCase()}
                  </td>
                  <td>
                    <strong style={{ fontSize: 13 }}>{t.name}</strong>
                    <br />
                    <small style={{ color: '#999' }}>{t.email}</small>
                  </td>
                  <td>
                    <span style={{
                      background: '#f0f0ff', color: '#6c63ff',
                      padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                    }}>
                      {t.category}
                    </span>
                  </td>
                  <td style={{ maxWidth: 200 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.subject}
                    </div>
                    <div style={{ fontSize: 12, color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.message.substring(0, 60)}…
                    </div>
                  </td>
                  <td>{statusBadge(t.status)}</td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap', color: '#888' }}>
                    {new Date(t.createdAt).toLocaleDateString()}<br />
                    <span style={{ fontSize: 11 }}>{new Date(t.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className={styles.editBtn} onClick={() => openDetail(t)}>
                        View
                      </button>
                      <button className={styles.deleteBtn} onClick={() => handleDelete(t._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
          <button className={styles.filterBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ lineHeight: '32px', fontSize: 13 }}>Page {page} of {pages}</span>
          <button className={styles.filterBtn} disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className={styles.overlay} onClick={() => setSelected(null)}>
          <div
            className={styles.modal}
            style={{ maxWidth: 560 }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17 }}>
                  Ticket #{selected._id.slice(-6).toUpperCase()}
                </h3>
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                  {new Date(selected.createdAt).toLocaleString()}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#aaa' }}
              >×</button>
            </div>

            {/* User info */}
            <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{selected.name}</div>
              <div style={{ fontSize: 12, color: '#888' }}>{selected.email}</div>
              <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                <span style={{ background: '#f0f0ff', color: '#6c63ff', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>
                  {selected.category}
                </span>
                {statusBadge(selected.status)}
              </div>
            </div>

            {/* Subject + message */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 4 }}>
                {selected.subject}
              </div>
              <div style={{
                background: '#f5f5f5', borderRadius: 8, padding: '12px 14px',
                fontSize: 14, color: '#333', lineHeight: 1.7, whiteSpace: 'pre-wrap',
                maxHeight: 180, overflowY: 'auto',
              }}>
                {selected.message}
              </div>
            </div>

            {/* Status update */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>
                Update Status
              </label>
              <select
                value={selected.status}
                onChange={e => setSelected(s => ({ ...s, status: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, width: '100%' }}
              >
                <option value="open">🟡 Open</option>
                <option value="in_progress">🔵 In Progress</option>
                <option value="resolved">✅ Resolved</option>
              </select>
            </div>

            {/* Admin note */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>
                Admin Note (internal)
              </label>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={3}
                placeholder="Add an internal note..."
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: '1px solid #ddd', fontSize: 13, resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
            </div>

            <div className={styles.modalActions}>
              <button className={styles.deleteBtn} onClick={() => handleDelete(selected._id)}>
                Delete
              </button>
              <button className={styles.cancelBtn} onClick={() => setSelected(null)}>
                Cancel
              </button>
              <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
